import { ref, watch, onMounted, onBeforeUnmount } from 'vue';
const DEFAULT_CACHE_TTL = 30 * 60 * 1000;
export function useInstagramFeed(options) {
    const { apiClient, target, consentGranted = false, cacheKey = 'instagram-feed', cacheTtlMs = DEFAULT_CACHE_TTL } = options;
    const isLoading = ref(false);
    const error = ref(null);
    const items = ref([]);
    const meta = ref(null);
    const hasFetched = ref(false);
    const observer = ref(null);
    const resolveConsent = () => (typeof consentGranted === 'boolean' ? consentGranted : consentGranted.value);
    const loadFromCache = () => {
        if (typeof window === 'undefined')
            return null;
        try {
            const raw = window.sessionStorage.getItem(cacheKey);
            if (!raw)
                return null;
            const cached = JSON.parse(raw);
            if (Date.now() - cached.timestamp > cacheTtlMs) {
                window.sessionStorage.removeItem(cacheKey);
                return null;
            }
            return cached.payload;
        }
        catch {
            return null;
        }
    };
    const saveToCache = (payload) => {
        if (typeof window === 'undefined')
            return;
        const cached = { timestamp: Date.now(), payload };
        window.sessionStorage.setItem(cacheKey, JSON.stringify(cached));
    };
    const applyPayload = (payload) => {
        if (!payload)
            return;
        meta.value = {
            consentRequired: payload.consentRequired,
            enabled: payload.enabled,
            reviewRequiredMessage: payload.reviewRequiredMessage,
            settings: payload.settings
        };
        items.value = payload.items || [];
    };
    const fetchFeed = async () => {
        if (isLoading.value || hasFetched.value)
            return;
        if (!resolveConsent()) {
            error.value = null;
            return;
        }
        const cached = loadFromCache();
        if (cached) {
            applyPayload(cached);
            hasFetched.value = true;
            return;
        }
        try {
            isLoading.value = true;
            error.value = null;
            const response = await apiClient.getInstagramFeed(true);
            if (response.status === 'success' && response.data) {
                applyPayload(response.data);
                saveToCache(response.data);
                hasFetched.value = true;
            }
            else {
                error.value = response.message || 'Instagram Feed konnte nicht geladen werden.';
            }
        }
        catch (fetchError) {
            error.value = fetchError.message || 'Instagram Feed konnte nicht geladen werden.';
        }
        finally {
            isLoading.value = false;
        }
    };
    const cleanupObserver = () => {
        if (observer.value) {
            observer.value.disconnect();
            observer.value = null;
        }
    };
    const initObserver = () => {
        cleanupObserver();
        if (!target.value || !resolveConsent())
            return;
        observer.value = new IntersectionObserver((entries, obs) => {
            for (const entry of entries) {
                if (entry.isIntersecting) {
                    void fetchFeed();
                    obs.disconnect();
                    observer.value = null;
                    break;
                }
            }
        }, { threshold: 0.1, rootMargin: '0px' });
        observer.value.observe(target.value);
    };
    if (typeof consentGranted !== 'boolean') {
        watch(consentGranted, (granted) => {
            if (granted) {
                if (!hasFetched.value)
                    initObserver();
            }
            else {
                items.value = [];
                meta.value = null;
                hasFetched.value = false;
                cleanupObserver();
            }
        }, { immediate: true });
    }
    onMounted(() => {
        if (typeof consentGranted === 'boolean' && !consentGranted)
            return;
        initObserver();
    });
    onBeforeUnmount(() => {
        cleanupObserver();
    });
    return {
        isLoading,
        error,
        items,
        meta,
        hasFetched,
        refetch: async () => {
            hasFetched.value = false;
            cleanupObserver();
            if (resolveConsent()) {
                await fetchFeed();
            }
        }
    };
}
